import { Modal } from "react-bootstrap";
import { forwardRef, ReactNode, useEffect, useImperativeHandle, useRef } from "react";
import { BrowserMultiFormatReader, IScannerControls } from "@zxing/browser";

export type BarcodeScannerProps = {
    onError(error: unknown): void;
    onHide?(): void;
    onResult(result: string): void;
    show: boolean;
    title?: ReactNode;
};

const Viewfinder = () => {
    return (
        <div className="viewfinder">
            <div
                className="corner-y"
                style={{
                    top: "-5px",
                    left: "0px",
                }}
            />
            <div
                className="corner-y"
                style={{
                    top: "-5px",
                    right: "0px",
                }}
            />
            <div
                className="corner-y"
                style={{
                    bottom: "-5px",
                    left: "0px",
                }}
            />
            <div
                className="corner-y"
                style={{
                    bottom: "-5px",
                    right: "0px",
                }}
            />
            <div
                className="corner-x"
                style={{
                    top: "-5px",
                    left: "-5px",
                }}
            />
            <div
                className="corner-x"
                style={{
                    bottom: "-5px",
                    left: "-5px",
                }}
            />
            <div
                className="corner-x"
                style={{
                    top: "-5px",
                    right: "-5px",
                }}
            />
            <div
                className="corner-x"
                style={{
                    bottom: "-5px",
                    right: "-5px",
                }}
            />
        </div>
    );
};

export const BarcodeScanner = forwardRef<HTMLVideoElement, BarcodeScannerProps>((p, ref) => {
    const { onError, onHide, onResult, show, title } = p;
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const readerRef = useRef<BrowserMultiFormatReader | null>(
        new BrowserMultiFormatReader(undefined, {
            delayBetweenScanAttempts: 1000, // ms
            delayBetweenScanSuccess: 1000, // ms
        })
    );
    const controlsRef = useRef<IScannerControls | null>(null);

    useImperativeHandle(ref, () => {
        // return the video element if it exists or return a dummy object
        return videoRef.current ?? (null as unknown as HTMLVideoElement);
    });

    useEffect(() => {
        if (!videoRef.current || !readerRef.current) return;
        readerRef.current
            .decodeFromVideoDevice(undefined, videoRef.current, (result, err, controls) => {
                controlsRef.current = controls;
                if (result) {
                    onResult(result.getText());
                }
                if (err) {
                    if (videoRef.current == null) {
                        controls.stop();
                        return;
                    }
                    onError(err.message);
                }
            })
            .catch((err) => onError(err));
        return () => {
            if (!show) {
                controlsRef.current?.stop();
                videoRef.current = null;
            }
        };
    }, [onError, onResult, show]);

    return show ? (
        <Modal show fullscreen onHide={onHide}>
            <Modal.Header closeButton>
                <Modal.Title>{title ?? "Scan Barcodes"}</Modal.Title>
            </Modal.Header>
            <Modal.Body className="barcode-scanner">
                <video ref={videoRef} />
                <Viewfinder />
            </Modal.Body>
        </Modal>
    ) : null;
});
